import _ from 'lodash';
import apiDuck, { PROCESSING_FLAG } from 'shared/lib/apiDuck';
import { upsertEntity, upsertEntities, updateEntityAttribute } from 'shared/lib/normalizer';
import { isJobApplicationInactive } from 'shared/constants';

const { createReducer, createLoadEntitiesActions, createProcessEntityActions, getEntities } = apiDuck('applications');
const [LOAD_ALL, LOADING_ALL, LOADED_ALL, LOAD_ALL_FAILED] = createLoadEntitiesActions('ALL');
const [ADD_CHANGE, ADDING_CHANGE, ADDED_CHANGE, , ADD_CHANGE_FINALLY] = createProcessEntityActions('ADD_CHANGE');
const [MARK_READ, , MARKED_READ] = createProcessEntityActions('MARK_READ');
const [ANSWER_FOLLOW_UP, , ANSWERED_FOLLOW_UP] = createProcessEntityActions('ANSWER_FOLLOW_UP');

// an own typed placeholder (follow-up answers) into the loaded history; the WS echo of the
// same change is deduped by id
const prependChange = (state, id, change) => {
  const entity = state.entities[id];
  if (!entity || !entity.changes || entity.changes.find(ch => ch.id === change.id)) {
    return state;
  }
  return {
    ...state,
    entities: {
      ...state.entities,
      [id]: {
        ...entity,
        changes: [change, ...entity.changes],
        changesStats: { ...entity.changesStats, total: entity.changesStats.total + 1, last: change.createdAt },
      }
    }
  };
};
const ADD_APPLICATION = 'APPLICATIONS_ADD_APPLICATION';
const ADDING_APPLICATION = 'APPLICATIONS_ADDING_APPLICATION';
export const ADDED_APPLICATION = 'APPLICATIONS_ADDED_APPLICATION';
const ADD_APPLICATION_FINALLY = 'APPLICATIONS_ADD_APPLICATION_FINALLY';

const reducer = createReducer((state, action) => {
  switch (action.type) {
    case LOADED_ALL: {
      return upsertEntities(state, action.result.map(entity => ({
        ...entity,
        changesStats: {
          ...entity.changesStats,
          last: new Date(entity.changesStats.last)
        },
        createdAt: new Date(entity.createdAt)
      })));
    }

    case 'JOB_APPLICATION_CHANGE@UPDATE':
    case ADDED_CHANGE: {
      const data = action.type === ADDED_CHANGE ? action.result : action.payload;
      const jobApplicationId = data.jobApplicationId;

      const exists = state.entities[jobApplicationId] && state.entities[jobApplicationId].changes && !!state.entities[jobApplicationId].changes.find(ch => ch.id === data.change.id);
      if (exists) {
        return state;
      }

      let next = state;
      if (data.statusId) {
        next = updateEntityAttribute(next, jobApplicationId, 'statusId', data.statusId);
      }

      if (state.entities[jobApplicationId] && state.entities[jobApplicationId].readAt === null) {
        next = updateEntityAttribute(next, jobApplicationId, 'readAt', data.change.createdAt);
      }

      // TODO: vyhodit po oprave SKLT-456
      if (!next.entities[jobApplicationId]) {
        return next;
      }

      return {
        ...next,
        entities: {
          ...next.entities,
          [jobApplicationId]: {
            ...next.entities[jobApplicationId],
            changes: next.entities[jobApplicationId].changes ? [data.change, ...next.entities[jobApplicationId].changes] : next.entities[jobApplicationId].changes,
            changesStats: {
              unread: action.type === ADDED_CHANGE
                || data.change.readAt !== null
                || !(data.change.message || data.change.type)
                || !data.change.user.company
                ? next.entities[jobApplicationId].changesStats.unread
                : next.entities[jobApplicationId].changesStats.unread + 1,
              total: next.entities[jobApplicationId].changesStats.total + 1,
              last: data.change.createdAt
            }
          }
        }
      };
    }

    case ADDING_APPLICATION: {
      return {
        ...state,
        [PROCESSING_FLAG]: true
      };
    }

    case ADDED_APPLICATION: {
      return upsertEntity(state, action.result);
    }

    case ADD_APPLICATION_FINALLY: {
      return {
        ...state,
        [PROCESSING_FLAG]: null
      };
    }

    // the recruiter sent follow-up questions: the content of the history placeholder (the
    // placeholder itself and the unread count come through JOB_APPLICATION_CHANGE)
    case 'JOB_APPLICATION_FOLLOW_UP@UPDATE': {
      const { jobApplicationId, followUp } = action.payload;
      return updateEntityAttribute(state, jobApplicationId, 'followUp', followUp);
    }

    // the user's answers: the response carries the answered followUp and its history placeholder
    case ANSWERED_FOLLOW_UP: {
      const { followUp, change } = action.result;
      return prependChange(updateEntityAttribute(state, action.payload.id, 'followUp', followUp), action.payload.id, change);
    }

    case MARKED_READ:
    case 'JOB_APPLICATION_CHANGES_READ@UPDATE': {
      const id = action.type === MARKED_READ ? action.payload.id : action.payload.applicationId;

      if (!(state.entities[id] && state.entities[id].changes)) {
        return state;
      }

      const readAt = (new Date()).toISOString();
      return {
        ...state,
        entities: {
          ...state.entities,
          [id]: {
            ...state.entities[id],
            changes: state.entities[id].changes.map(change => ({ ...change, readAt })),
            changesStats: {
              ...state.entities[id].changesStats,
              unread: 0,
            }
          }
        }
      };
    }

    default:
      return state;
  }
});

export default reducer;

export const actions = {
  loadAllDetails() {
    return {
      type: LOAD_ALL,
      types: [LOADING_ALL, LOADED_ALL, LOAD_ALL_FAILED],
      callApi: client => client.get('/user/jobs/applications'),
    };
  },
  addChange(jobId, applicationId, payload, onSuccess, onFailure) {
    return {
      type: ADD_CHANGE,
      types: [ADDING_CHANGE, ADDED_CHANGE, null, ADD_CHANGE_FINALLY],
      callApi: client => client.post(`/user/jobs/${jobId}/applications/${applicationId}`, payload),
      payload: {
        id: applicationId
      },
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  addApplication(loggedIn, jobId, payload, onSuccess, onFailure) {
    return {
      type: ADD_APPLICATION,
      types: [
        ADDING_APPLICATION,
        loggedIn ? ADDED_APPLICATION : null,
        null,
        ADD_APPLICATION_FINALLY
      ],
      callApi: client => client.post(`/user/jobs/${jobId}/applications`, payload),
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
  markChangesRead(jobId, applicationId) {
    return {
      type: MARK_READ,
      types: [null, MARKED_READ],
      silent401Error: true,
      callApi: client => client.post(`/user/jobs/${jobId}/applications/${applicationId}/changes-read`),
      payload: {
        id: applicationId
      },
    };
  },
  // answers = [{ id, answer }], one-time; the response is the answered followUp
  answerFollowUp(jobId, applicationId, answers, onSuccess, onFailure) {
    return {
      type: ANSWER_FOLLOW_UP,
      types: [null, ANSWERED_FOLLOW_UP],
      callApi: client => client.post(`/user/jobs/${jobId}/applications/${applicationId}/follow-up`, { answers }),
      payload: {
        id: applicationId
      },
      onSuccess: onSuccess ? [onSuccess] : [],
      onFailure: onFailure ? [onFailure] : []
    };
  },
};

export const selectors = {
  areApplicationDetailsLoaded(state) {
    // initial list is loaded in bulk, so no ids means no applications yet
    if (state.applications.ids.length === 0) {
      return true;
    }

    // any application detail should have at least 1 change
    return Array.isArray(state.applications.entities[state.applications.ids[0]].changes);
  },

  getActiveApplicationByJobId(state, jobId) {
    return getEntities(state, entity => entity.job.id === jobId && !isJobApplicationInactive(entity.statusId)).entities[0] || null;
  },

  getApplicationById(state, applicationId) {
    return state.applications.entities[applicationId] || null;
    // return getEntities(state, entity => entity.job.slug === jobSlug).entities[0] || null;
  },

  getAllApplications(state) {
    const applications = getEntities(state);

    return {
      ...applications,
      entities: applications.entities.sort((a, b) => new Date(b.changesStats.last) > new Date(a.changesStats.last) ? 1 : -1)
    };
  },

  getUnreadChangesCount(state) {
    return _.values(state.applications.entities).reduce((acc, curr) => acc + curr.changesStats.unread, 0);
  }
};
